import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { useAuth } from "@/auth/AuthContext";
import { Modal } from "@/pages/Employees";
import ActionableNotificationModal from "@/components/ActionableNotificationModal";
import { Megaphone, Check, Bell, PartyPopper, Calendar, Repeat2, Clock } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function Notifications() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [unread, setUnread] = useState(0);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcast, setBroadcast] = useState({ title: "", body: "", target: "all", role: "employee", email: false });
  // Fase 3 (Feb 2026) — actionable notifications open a modal on the same
  // page. `actionable` holds { kind, data } once resolved from the API.
  const [actionable, setActionable] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);
  const isMgr = ["owner", "manager", "teamlead"].includes(user?.role);

  const ACTIONABLE_TYPES = { leave: "leave", swap: "swap", time: "time" };

  const openActionable = async (n) => {
    const kind = ACTIONABLE_TYPES[n.type];
    if (!kind) return;
    setLoadingAction(true);
    try {
      // Fase 3.1 (Feb 2026) — resolve the exact target via the notification
      // itself, never a "newest pending" heuristic. Tenant-scope + authz are
      // re-checked server-side.
      try {
        const { data } = await api.get(`/notifications/${n._id}/target`);
        if (data?.data) setActionable({ kind, data: data.data });
      } catch (err) {
        if (err?.response?.status === 410) {
          // Fase 3.2 — legacy notif: show a read-only info modal on the same
          // page instead of just a toast.
          setActionable({ kind: "legacy", data: n });
        } else if (err?.response?.status === 404) {
          toast.error(t("notif_target_missing"));
        } else {
          toast.error(t("notif_target_error"));
        }
      }
      if (!n.read) markOne(n._id);
    } finally {
      setLoadingAction(false);
    }
  };

  const load = async () => {
    const { data } = await api.get("/notifications");
    setList(data.items || []); setUnread(data.unread || 0);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  const markOne = async (id) => { await api.post(`/notifications/${id}/read`); load(); };

  const celebrate = async (n) => {
    // Optimistic update
    setList((prev) => prev.map((x) => x._id === n._id ? {
      ...x,
      celebrated: !x.celebrated,
      celebrations_count: (x.celebrations_count || 0) + (x.celebrated ? -1 : 1),
    } : x));
    try {
      await api.post(`/notifications/${n._id}/celebrate`);
    } catch (e) {
      toast.error(t("error"));
      load();
    }
  };

  const sendBroadcast = async () => {
    try {
      await api.post("/notifications/broadcast", broadcast);
      toast.success(t("saved")); setShowBroadcast(false); load();
    } catch { toast.error(t("error")); }
  };

  return (
    <div data-testid="page-notifications">
      <PageHeader
        eyebrow={t("notifications")}
        title={t("notifications")}
        subtitle={`${unread} ${t("unread")}`}
        actions={
          <>
            {isMgr && (
              <button onClick={() => setShowBroadcast(true)} className="btn btn-ghost" data-testid="broadcast-open">
                <Megaphone size={14} />{t("broadcast")}
              </button>
            )}
            <button onClick={markAll} className="btn btn-primary" data-testid="mark-all-read"><Check size={14} />{t("mark_all_read")}</button>
          </>
        }
      />
      {/* Hero summary — consistent look with Team Vandaag / Aanvragen */}
      <div
        className="card p-5 mb-4 flex items-center gap-4"
        style={{ background: "linear-gradient(135deg, hsl(var(--primary-soft)) 0%, hsl(var(--surface)) 60%)" }}
        data-testid="notif-hero"
      >
        <div
          className="w-12 h-12 rounded-2xl grid place-items-center shrink-0 text-white"
          style={{
            background: "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(217 91% 45%) 100%)",
            boxShadow: "0 12px 28px -10px hsl(var(--primary) / 0.55)",
          }}
        >
          <Bell size={20} strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[22px] font-bold text-[hsl(var(--text-strong))] leading-none font-mono">{unread}</div>
          <div className="text-[12px] font-semibold text-[hsl(var(--text-muted))] mt-1">
            {unread === 1 ? "ongelezen melding" : `${unread === 0 ? "Geen" : unread} ongelezen meldingen`}
          </div>
        </div>
        {unread > 0 && (
          <div className="flex items-center gap-1.5 text-[10px] font-semibold text-[hsl(var(--success))] bg-[hsl(var(--success-soft))] px-2 py-1 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--success))] animate-pulse" />
            NIEUW
          </div>
        )}
      </div>

      {list.length === 0 ? (
        <EmptyState title={t("empty_notifs_title")} hint={t("empty_notifs_hint")} />
      ) : (
        <div className="flex flex-col gap-2">
          {list.map((n) => {
            // Fase 3 — softer visual tokens per notification type; no more
            // saturated 4px vertical rail. Icon-tinted background is enough.
            const typeMeta = {
              leave:     { color: "hsl(280 55% 55%)", Icon: Calendar },
              swap:      { color: "hsl(217 82% 55%)", Icon: Repeat2 },
              shift:     { color: "hsl(217 82% 55%)", Icon: Bell },
              news:      { color: "hsl(160 55% 45%)", Icon: Megaphone },
              broadcast: { color: "hsl(160 55% 45%)", Icon: Megaphone },
              birthday:  { color: "hsl(340 72% 55%)", Icon: PartyPopper },
              billing:   { color: "hsl(38 85% 50%)",  Icon: Bell },
              time:      { color: "hsl(200 65% 50%)", Icon: Clock },
              user_deactivated: { color: "hsl(0 65% 55%)", Icon: Bell },
            };
            const { color: typeColor, Icon: TypeIcon } = typeMeta[n.type] || { color: "hsl(var(--text-muted))", Icon: Bell };
            const isActionable = !!ACTIONABLE_TYPES[n.type];
            return (
              <div
                key={n._id}
                className={`card p-3.5 flex items-start gap-3 relative transition-colors cursor-pointer ${
                  n.read ? "bg-[hsl(var(--bg))]" : "bg-[hsl(var(--surface))]"
                }`}
                style={{ borderColor: n.read ? "hsl(var(--border))" : "hsl(var(--border-strong))" }}
                onClick={() => {
                  if (isActionable) openActionable(n);
                  else if (!n.read) markOne(n._id);
                }}
                data-testid={`notif-${n._id}`}
                data-actionable={isActionable ? "true" : "false"}
              >
                <div
                  className="w-9 h-9 rounded-xl grid place-items-center shrink-0"
                  style={{ background: `color-mix(in srgb, ${typeColor} 12%, transparent)`, color: typeColor }}
                >
                  <TypeIcon size={15} strokeWidth={2.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <div className={`text-[13.5px] leading-tight flex-1 ${n.read ? "font-medium text-[hsl(var(--text-muted))]" : "font-semibold text-[hsl(var(--text-strong))]"}`}>{n.title}</div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-[hsl(var(--primary))] shrink-0 mt-1.5" aria-label="ongelezen" />}
                  </div>
                  {n.body && <div className={`text-[12.5px] mt-1 leading-snug ${n.read ? "text-[hsl(var(--text-muted))]" : "text-[hsl(var(--text))]"}`}>{n.body}</div>}
                  <div className="text-[10.5px] text-[hsl(var(--text-muted))] mt-1.5 font-mono">{new Date(n.created_at).toLocaleString("nl", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>

                  {/* Celebrate reaction — only on birthday notifications */}
                  {n.type === "birthday" && n.birthday_user_id && n.birthday_user_id !== user?._id && (
                    <div className="mt-2.5">
                      <motion.button
                        whileTap={{ scale: 0.94 }}
                        onClick={(e) => { e.stopPropagation(); celebrate(n); }}
                        data-testid={`celebrate-${n._id}`}
                        aria-pressed={!!n.celebrated}
                        className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12px] font-semibold border transition-colors
                          ${n.celebrated
                            ? "bg-[hsl(38_92%_96%)] text-[hsl(28_92%_42%)] border-[hsl(38_92%_84%)]"
                            : "bg-transparent text-[hsl(var(--text-strong))] border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"}
                        `}
                      >
                        <PartyPopper size={13} strokeWidth={2} />
                        <span>{n.celebrated ? t("celebrated") : t("celebrate")}</span>
                        {(n.celebrations_count || 0) > 0 && (
                          <span className="ml-0.5 tabular-nums text-[11px] font-bold text-[hsl(var(--text-muted))]">
                            · {n.celebrations_count}
                          </span>
                        )}
                      </motion.button>
                    </div>
                  )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {showBroadcast && (
        <Modal title={t("broadcast")} onClose={() => setShowBroadcast(false)}>
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1"><span className="eyebrow">{t("title")}</span>
              <input data-testid="bcast-title" value={broadcast.title} onChange={(e) => setBroadcast({ ...broadcast, title: e.target.value })} className={inp} /></label>
            <label className="flex flex-col gap-1"><span className="eyebrow">{t("body")}</span>
              <textarea data-testid="bcast-body" rows={4} value={broadcast.body} onChange={(e) => setBroadcast({ ...broadcast, body: e.target.value })} className={inp + " !h-auto py-2"} /></label>
            <label className="flex flex-col gap-1"><span className="eyebrow">{t("target")}</span>
              <select value={broadcast.target} onChange={(e) => setBroadcast({ ...broadcast, target: e.target.value })} className={inp}>
                <option value="all">{t("all")}</option>
                <option value="role">{t("by_role")}</option>
              </select></label>
            {broadcast.target === "role" && (
              <label className="flex flex-col gap-1"><span className="eyebrow">{t("role")}</span>
                <select value={broadcast.role} onChange={(e) => setBroadcast({ ...broadcast, role: e.target.value })} className={inp}>
                  <option value="employee">{t("role_employee")}</option>
                  <option value="teamlead">{t("role_teamlead")}</option>
                  <option value="manager">{t("role_manager")}</option>
                </select></label>
            )}
            <label className="flex items-center gap-2">
              <input type="checkbox" data-testid="bcast-email" checked={broadcast.email} onChange={(e) => setBroadcast({ ...broadcast, email: e.target.checked })} />
              <span className="text-sm">{t("also_email")}</span>
            </label>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setShowBroadcast(false)} className="btn btn-ghost">{t("cancel")}</button>
            <button onClick={sendBroadcast} className="btn btn-primary" data-testid="bcast-send">{t("send")}</button>
          </div>
        </Modal>
      )}
      {actionable && actionable.kind === "legacy" && (
        <div
          role="dialog"
          data-testid="legacy-notif-modal"
          className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setActionable(null)}
        >
          <div className="card w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="eyebrow">{t("legacy_notif_readonly_title")}</div>
            <div className="font-display text-[17px] font-bold text-[hsl(var(--text-strong))] mt-1">
              {actionable.data.title}
            </div>
            {actionable.data.body && (
              <div className="text-[13px] text-[hsl(var(--text))] mt-2">{actionable.data.body}</div>
            )}
            <div className="text-[11px] text-[hsl(var(--text-muted))] mt-2 font-mono">
              {new Date(actionable.data.created_at).toLocaleString("nl")}
            </div>
            <div className="mt-4 p-3 rounded-xl bg-[hsl(var(--muted))] text-[12px] text-[hsl(var(--text-muted))]">
              {t("legacy_notif_readonly_body")}
            </div>
            <div className="mt-4 flex justify-end">
              <button className="btn btn-quiet" onClick={() => setActionable(null)}>{t("close") || "Sluiten"}</button>
            </div>
          </div>
        </div>
      )}
      {actionable && actionable.kind !== "legacy" && (
        <ActionableNotificationModal
          kind={actionable.kind}
          data={actionable.data}
          onClose={() => setActionable(null)}
          onDecided={() => { setActionable(null); load(); }}
        />
      )}
      {loadingAction && null}
    </div>
  );
}
const inp = "input-field !h-10 !text-[13px] !rounded-xl min-w-0";
